import React from "react";
import PageContainer from "@/components/layouts/PageContainer";
import { Button } from "@/components/button";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { IS_PRODUCTION } from "@/utils/env";
import * as Sentry from "@sentry/react";
import { useQueryGetMe } from "@/apis/users/getMe";

const SentryTestPage: React.FC = () => {
  const { data: user } = useQueryGetMe();

  const handleTestError = () => {
    throw new Error("This is a test error for Sentry!");
  };

  const handleAsyncError = () => {
    setTimeout(() => {
      throw new Error("This is an async test error for Sentry!");
    }, 1000);
  };

  const handlePromiseError = () => {
    Promise.reject(new Error("This is a promise rejection error for Sentry!"));
  };

  const handleTestUserContext = () => {
    if (IS_PRODUCTION) {
      // 사용자 정보 설정 테스트
      Sentry.setUser({
        id: user?.id || "test-user",
        email: user?.email || "test@example.com",
        username: user?.name || "Test User",
        role: user?.role || "student",
      });

      // 커스텀 컨텍스트 설정
      Sentry.setContext("test_context", {
        test_scenario: "manual_user_test",
        timestamp: new Date().toISOString(),
        page: "sentry_test_page",
      });

      alert("User context set in Sentry!");
    } else {
      alert("Sentry is disabled in development mode");
    }
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <Heading>Sentry Error Testing</Heading>
          <Text className="text-zinc-500">
            Test various error scenarios to ensure Sentry integration is working
            properly.
          </Text>
        </div>

        {/* 환경 상태 표시 */}
        {!IS_PRODUCTION && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>ℹ️ Development Mode:</strong> Sentry is currently
              disabled. Errors will not be sent to Sentry. To test Sentry
              integration, build the application for production.
            </p>
          </div>
        )}

        {IS_PRODUCTION && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">
              <strong>✅ Production Mode:</strong> Sentry is active. Errors will
              be sent to your Sentry dashboard.
            </p>
          </div>
        )}

        {/* 현재 사용자 정보 표시 */}
        {user && (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="font-semibold mb-2">Current User Info</h4>
            <div className="text-sm space-y-1">
              <p>
                <strong>ID:</strong> {user.id}
              </p>
              <p>
                <strong>Name:</strong> {user.name}
              </p>
              <p>
                <strong>Email:</strong> {user.email}
              </p>
              <p>
                <strong>Role:</strong> {user.role}
              </p>
              {user.grade && (
                <p>
                  <strong>Grade:</strong> {user.grade}
                </p>
              )}
              {user.school && (
                <p>
                  <strong>School:</strong> {user.school}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Error Tests</h3>
          <div className="space-y-4">
            <div>
              <Button
                onClick={handleTestError}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Throw Synchronous Error
              </Button>
              <Text className="text-sm text-gray-600 mt-1">
                Throws an immediate error that should be caught by Sentry
              </Text>
            </div>

            <div>
              <Button
                onClick={handleAsyncError}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                Throw Async Error (setTimeout)
              </Button>
              <Text className="text-sm text-gray-600 mt-1">
                Throws an error after 1 second using setTimeout
              </Text>
            </div>

            <div>
              <Button
                onClick={handlePromiseError}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Promise Rejection Error
              </Button>
              <Text className="text-sm text-gray-600 mt-1">
                Creates an unhandled promise rejection
              </Text>
            </div>

            <div>
              <Button
                onClick={handleTestUserContext}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Test User Context
              </Button>
              <Text className="text-sm text-gray-600 mt-1">
                Sets user information and custom context in Sentry
              </Text>
            </div>
          </div>
        </div>

        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-sm">
            <strong>Note:</strong> These buttons will generate real errors.
            {IS_PRODUCTION
              ? "They will be sent to Sentry dashboard with user context."
              : "In development mode, they will only show in browser console."}
          </p>
        </div>
      </div>
    </PageContainer>
  );
};

export default SentryTestPage;
